import React, { useEffect } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, rtdb } from "./firebase"; // 👈 Import rtdb
import AuthForms from "./components/Auth/AuthForms";
import ChatUI from "./components/Chat/ChatUI";
import Home from "./components/Home/Home";
import AdminPanel from "./components/Admin/AdminPanel";
import AdminLogin from "./components/Admin/AdminLogin"; // 👈 Import AdminLogin
import RequireAdmin from "./components/Admin/RequireAdmin"; // 👈 Import RequireAdmin
import Loader from "./components/Loader"; // 👈 Import Loader
import { CallProvider } from "./context/CallContext"; // 👈 Import Provider (Fixed)
import PrivacyPolicy from "./components/Legal/PrivacyPolicy";
import TermsOfService from "./components/Legal/TermsOfService";
import Support from "./components/Support/Support"; // 👈 Import Support
import EmailVerificationPending from "./components/Auth/EmailVerificationPending"; // 👈 Import Verification Guard
import NotFound from "./components/NotFound"; // 👈 Import NotFound
import LinkDevice from "./components/Auth/LinkDevice"; // 👈 Import LinkDevice
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from "react-router-dom";

// 👇 Import the necessary functions from Firebase Realtime Database
import { ref, onValue, onDisconnect, set, serverTimestamp } from "firebase/database";
import { getToken } from "firebase/messaging"; // 👈 Import getToken
import { doc, updateDoc } from "firebase/firestore"; // 👈 Import FireStore functions
import { messaging, db } from "./firebase"; // 👈 Import messaging and db
import usePresence from "./hooks/usePresence"; // 👈 Import Presence Hook
import useKeySync from "./hooks/useKeySync"; // 👈 Import Key Sync Hook
import { ThemeProvider } from "./context/ThemeContext"; // 👈 Import ThemeProvider

// 🔗 Invite Link Handler
const JoinHandler = () => {
  const { code } = useParams();
  useEffect(() => {
    if (code) {
      sessionStorage.setItem("joinSquadCode", code);
    }
  }, [code]);
  return <Navigate to="/" replace />;
};

export default function App() {
  const [user, loading] = useAuthState(auth);


  useEffect(() => {
    if (!user) return;


    const checkSession = async () => {
      const { doc, onSnapshot, updateDoc, arrayUnion } = await import("firebase/firestore");
      const { db } = await import("./firebase");
      const { derivePublicKey } = await import("./utils/crypto");

      const unsub = onSnapshot(doc(db, "users", user.uid), async (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();

        // Security Check: Blocked User
        if (data.isBlocked) {
          alert("Your account has been blocked by an administrator.");
          auth.signOut();
          return;
        }

        // Security Check: 24-Hour Session Timeout
        if (user.metadata.lastSignInTime) {
          const lastSignIn = new Date(user.metadata.lastSignInTime).getTime();
          const twentyFourHours = 24 * 60 * 60 * 1000;
          if (Date.now() - lastSignIn > twentyFourHours) {
            alert("Session expired. For security, please log in again.");
            await auth.signOut();
            return;
          }
        }

        const deviceId = localStorage.getItem("deviceId");
        if (!deviceId) return;

        // 🔒 1 App + 1 Web Policy
        // We track 'activeWebSessionId' for Web browsers.
        const activeWebSessionId = data.activeWebSessionId;
        const isLoggingIn = sessionStorage.getItem("isLoggingIn");

        if (isLoggingIn || !activeWebSessionId) {
          if (activeWebSessionId !== deviceId) {
            // Claim Web Session
            updateDoc(doc(db, "users", user.uid), {
              activeWebSessionId: deviceId, // 💻 Web Specific
              lastActive: Date.now(),
              devices: arrayUnion({ // 💎 CRITICAL FIX: Use arrayUnion
                deviceId: deviceId,
                publicKey: data.publicKey || "",
                lastActive: Date.now()
              })
            }).catch(e => console.error("Session Claim Error", e));

            sessionStorage.removeItem("isLoggingIn");
          }
          return;
        }

        // 2. Monitoring
        // If activeWebSessionId exists and is NOT us, we have been kicked by another WEB session.
        if (activeWebSessionId && activeWebSessionId !== deviceId) {
          console.warn(`Session Mismatch: Remote Web ${activeWebSessionId} vs Local ${deviceId}`);
          alert("You have logged in on another Web Browser. This session has been terminated.");
          auth.signOut();
        }

      });

      return unsub;
    };

    const unsubPromise = checkSession();

    return () => {
      unsubPromise.then(unsub => unsub && unsub());
    };
  }, [user]);




  // ✨ ADD THIS USEEFFECT HOOK FOR PRESENCE MANAGEMENT ✨
  // ✨ Presence Management Hook
  usePresence(user);

  // 🔑 Key Synchronization Hook (Fixes E2EE Mismatches)
  useKeySync(user);

  // 🔔 FCM Token Registration (Web Push)
  useEffect(() => {
    if (!user) return;

    const registerToken = async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission === "granted") {
          const currentToken = await getToken(messaging, {
            vapidKey: "BMwB_v70k2gJp4f6L8yX7r5q9a0b1c2d3e4f5g6h7i8j9k0l1m2n3o4p5" // Replace if you have a specific VAPID key pair, else Firebase uses default
            // Actually, for simple setups, we might not need VAPID key if using default config, but it's recommended. 
            // I will use an empty option object if unsure, or typically 
            // "BM..." is a placeholder. 
            // Better to try without vapidKey first if not generated, or assume standard.
            // Let's use the one from firebase-messaging-sw.js logic or omit to let Firebase handle it.
            // Safe bet: omit VAPID unless user provided one.
          });

          if (currentToken) {
            // console.log("FCM Token:", currentToken);
            await updateDoc(doc(db, "users", user.uid), {
              fcmToken: currentToken
            });
          }
        }
      } catch (err) {
        console.warn("FCM Token Error:", err);
      }
    };

    registerToken();
  }, [user]);


  if (loading) return <Loader text="Initializing ChatIQ..." />;

  return (
    <ThemeProvider> {/* 👈 Theme Provider */}
      <Router>
        <CallProvider> {/* 👈 Wrap Global Context */}
          <Routes>
            <Route path="/" element={
              user ? (
                user.emailVerified ? <ChatUI /> : <EmailVerificationPending user={user} />
              ) : (
                <Home />
              )
            } />
            <Route path="/join/:code" element={<JoinHandler />} />
            <Route path="/login" element={!user ? <AuthForms /> : <Navigate to="/" />} />
            <Route path="/link-device" element={<LinkDevice />} />
            {/* Note: Modified to allow access without auth for the Login flow */}
            <Route path="/link-device-start" element={<LinkDevice />} />

            {/* Legal Routes */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/support" element={<Support />} /> {/* 👈 Add Support Route */}

            {/* Admin Routes */}
            <Route path="/admin" element={<Navigate to="/admin/dashboard" />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin/dashboard"
              element={
                <RequireAdmin>
                  <AdminPanel />
                </RequireAdmin>
              }
            />

            {/* 🛑 404 Catch-All */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </CallProvider>
      </Router>
    </ThemeProvider>
  );
}
